import type {
  Lci3Report,
  Lci3SalesMetrics,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import type { Lci3DimensionTableData } from '@features/lci-report/models/lci3-types';
import type { Slide, TableCell } from '@features/powerpoint';
import type { Subdimension } from '@features/reports/subdomains/visitation';
import type { FormSchema } from '../../../../../../components/form-schema';
import { formatters } from '@utils/formattersV2';
import { themeV2 } from '../../../../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../../../../utils/constants-v2';
import { generateTableV2 } from '../../../../../../utils/build-table-v2';
import { contentSlideHeaderV2 } from '../../../../../../utils/slide-header-v2';
import { groupDimensionData } from './domain/flatten-dimension-data';

const SUBDIMENSIONS_PER_SLIDE = 8;

const TABLE_HEADER_OPTIONS: TableCell['options'] = {
  color: themeV2.colors.metricBlue,
  bold: true,
  fontFace: DEFAULT_FONT,
};

const salesLiftColumnLabels: TableCell[] = [
  { text: 'Dimension', options: TABLE_HEADER_OPTIONS },
  { text: 'Total Impressions', options: TABLE_HEADER_OPTIONS },
  { text: 'Unique Users', options: TABLE_HEADER_OPTIONS },
  { text: 'Incremental Sales Lift', options: TABLE_HEADER_OPTIONS },
  { text: 'Incremental Sales', options: TABLE_HEADER_OPTIONS },
  { text: 'Transaction Size Lift', options: TABLE_HEADER_OPTIONS },
  { text: 'Transaction Rate Lift', options: TABLE_HEADER_OPTIONS },
  { text: 'Penetration Lift', options: TABLE_HEADER_OPTIONS },
];

const formatRow = (row: Subdimension): TableCell[] => [
  { text: row.value },
  {
    text: formatters.impressions(row.visitMetrics.impressions, { full: true }),
  },
  { text: formatters.users(row.visitMetrics.uniqueUsers, { full: true }) },
  { text: formatters.lift(row.salesMetrics?.incrementalSalesLift.total) },
  {
    text: formatters.currency(row.salesMetrics?.incrementalSales.total, {
      full: true,
    }),
  },
  {
    text: formatters.percentage(row.salesMetrics?.transactionSizeLift.total, {
      decimals: 1,
    }),
  },
  {
    text: formatters.percentage(row.salesMetrics?.transactionRateLift.total, {
      decimals: 1,
    }),
  },
  {
    text: formatters.percentage(row.salesMetrics?.penetrationLift.total, {
      decimals: 1,
    }),
  },
];

const formatOverallRow = (
  overallVisitMetrics: Lci3Report['visitMetrics'],
  overallSalesMetrics: Lci3SalesMetrics
): TableCell[] => [
  { text: 'Overall', options: { bold: true } },
  {
    text: formatters.impressions(overallVisitMetrics.impressions, {
      full: true,
    }),
  },
  {
    text: formatters.users(overallVisitMetrics.uniqueUsers, { full: true }),
  },
  { text: formatters.lift(overallSalesMetrics.incrementalSalesLift.total) },
  {
    text: formatters.currency(overallSalesMetrics.incrementalSales.total, {
      full: true,
    }),
  },
  {
    text: formatters.percentage(overallSalesMetrics.transactionSizeLift.total, {
      decimals: 1,
    }),
  },
  {
    text: formatters.percentage(overallSalesMetrics.transactionRateLift.total, {
      decimals: 1,
    }),
  },
  {
    text: formatters.percentage(overallSalesMetrics.penetrationLift.total, {
      decimals: 1,
    }),
  },
];

type SalesLiftSummaryTableV2Props = {
  slideMaster: string;
  overallVisitMetrics: Lci3Report['visitMetrics'];
  overallSalesMetrics: Lci3SalesMetrics;
  dimensionTableData: Lci3DimensionTableData;
  customFields: FormSchema['dimensions'];
};

export const salesLiftSummaryTableV2 = (
  props: SalesLiftSummaryTableV2Props
): Slide[] => {
  const dimensionGroups = groupDimensionData(props.dimensionTableData);
  const overallRow = formatOverallRow(
    props.overallVisitMetrics,
    props.overallSalesMetrics
  );

  return dimensionGroups.flatMap(group => {
    const subdimensionRows = group.subdimensions.map(row => formatRow(row));
    const totalPages = Math.max(
      1,
      Math.ceil(subdimensionRows.length / SUBDIMENSIONS_PER_SLIDE)
    );

    const customHeader =
      props.customFields[group.slug as keyof FormSchema['dimensions']]
        .salesTableHeader;

    return Array.from({ length: totalPages }, (_, pageIndex) => {
      const startIndex = pageIndex * SUBDIMENSIONS_PER_SLIDE;
      const pageRows = subdimensionRows.slice(
        startIndex,
        startIndex + SUBDIMENSIONS_PER_SLIDE
      );

      const defaultTitle = `Sales Lift Summary Table - By ${group.dimensionName} (${pageIndex + 1}/${totalPages})`;
      const title = customHeader.length > 0 ? customHeader : defaultTitle;

      return {
        slideMaster: props.slideMaster,
        tables: [
          generateTableV2(salesLiftColumnLabels, [overallRow, ...pageRows], {
            w: 12.35,
            x: 0.33,
            y: 1.64,
            rowH: 0.35,
            fontSize: 10,
          }),
        ],
        texts: [contentSlideHeaderV2(title)],
      };
    });
  });
};
